"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMedicineCertificates } from "@/entities/certified-medicine";
import { fetchMedicineAnalogues, getMedicineDetails, getMedicineIcdCodes, getMedicineVariations } from "@/entities/medicine/api/medicines";
import { getMedicineDisplayName } from "@/entities/medicine/lib/medicine-label";
import { MedicineDetailsCard } from "@/entities/medicine/ui/medicine-details-card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/ui/dialog";
import { Skeleton } from "@/shared/ui/skeleton";
const MIN_LOADING_MS = 500;
type MedicineDetailsRequestedTab = "analogues";
const DETAIL_TEXT_FIELDS = ["indications", "contraindications", "dosage_instructions", "side_effects", "storage_conditions"] as const;
type MedicineDetailsDialogProps = {
  medicineId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onActiveMedicineChange?: (medicineId: string) => void;
  requestedTab?: MedicineDetailsRequestedTab | null;
  onRequestedTabChange?: (tab: MedicineDetailsRequestedTab | null) => void;
};
export function MedicineDetailsDialog(t0) {
  const $ = _c(86);
  const {
    medicineId,
    open,
    onOpenChange,
    onActiveMedicineChange,
    requestedTab: t1,
    onRequestedTabChange
  } = t0;
  const requestedTab = t1 === undefined ? null : t1;
  const {
    t
  } = useTranslation();
  const [activeId, setActiveId] = React.useState(medicineId);
  let t2;
  let t3;
  if ($[0] !== medicineId) {
    t2 = () => {
      setActiveId(medicineId);
    };
    t3 = [medicineId];
    $[0] = medicineId;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  React.useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== open) {
    t4 = () => {
      if (!open) {
        setActiveId(null);
      }
    };
    t5 = [open];
    $[3] = open;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  React.useEffect(t4, t5);
  const [activeRequestedTab, setActiveRequestedTab] = React.useState(requestedTab);
  let t6;
  let t7;
  if ($[6] !== requestedTab) {
    t6 = () => {
      if (requestedTab === "analogues") {
        setActiveRequestedTab(requestedTab);
      }
    };
    t7 = [requestedTab];
    $[6] = requestedTab;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  React.useEffect(t6, t7);
  let t8;
  let t9;
  if ($[9] !== open) {
    t8 = () => {
      if (!open) {
        setActiveRequestedTab(null);
      }
    };
    t9 = [open];
    $[9] = open;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  React.useEffect(t8, t9);
  let t10;
  if ($[12] !== onActiveMedicineChange) {
    t10 = nextMedicineId => {
      setActiveId(nextMedicineId);
      onActiveMedicineChange?.(nextMedicineId);
    };
    $[12] = onActiveMedicineChange;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const handleSelectVariation = t10;
  let t11;
  if ($[14] !== onActiveMedicineChange) {
    t11 = nextMedicineId_0 => {
      setActiveId(nextMedicineId_0);
      onActiveMedicineChange?.(nextMedicineId_0);
    };
    $[14] = onActiveMedicineChange;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const handleSelectAnalogue = t11;
  let t12;
  if ($[16] !== onRequestedTabChange) {
    t12 = nextTab => {
      setActiveRequestedTab(nextTab);
      onRequestedTabChange?.(nextTab);
    };
    $[16] = onRequestedTabChange;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const handleRequestedTabChange = t12;
  let t13;
  if ($[18] !== activeId) {
    t13 = ["dashboard", "medicines", "details", activeId];
    $[18] = activeId;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const t14 = open && activeId != null;
  let t15;
  if ($[20] !== activeId) {
    t15 = () => getMedicineDetails(activeId as string);
    $[20] = activeId;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t13 || $[23] !== t14 || $[24] !== t15) {
    t16 = {
      queryKey: t13,
      enabled: t14,
      queryFn: t15
    };
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const detailsQuery = useQuery(t16);
  let t17;
  if ($[26] !== activeId) {
    t17 = ["dashboard", "medicines", "icd-codes", activeId];
    $[26] = activeId;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const t18 = open && activeId != null;
  let t19;
  if ($[28] !== activeId) {
    t19 = () => getMedicineIcdCodes(activeId as string);
    $[28] = activeId;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== t17 || $[31] !== t18 || $[32] !== t19) {
    t20 = {
      queryKey: t17,
      enabled: t18,
      queryFn: t19
    };
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  const icdCodesQuery = useQuery(t20);
  let t21;
  if ($[34] !== activeId) {
    t21 = ["dashboard", "medicines", "variations", activeId];
    $[34] = activeId;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  const t22 = open && activeId != null;
  let t23;
  if ($[36] !== activeId) {
    t23 = () => getMedicineVariations(activeId as string);
    $[36] = activeId;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== t21 || $[39] !== t22 || $[40] !== t23) {
    t24 = {
      queryKey: t21,
      enabled: t22,
      queryFn: t23
    };
    $[38] = t21;
    $[39] = t22;
    $[40] = t23;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  const variationsQuery = useQuery(t24);
  let t25;
  if ($[42] !== detailsQuery.data) {
    t25 = detailsQuery.data != null && Boolean(detailsQuery.data.atc_code || detailsQuery.data.international_name) && DETAIL_TEXT_FIELDS.every(field => !detailsQuery.data?.[field]);
    $[42] = detailsQuery.data;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  const analoguesAreDefaultTab = t25;
  let t26;
  if ($[44] !== activeId) {
    t26 = ["dashboard", "medicines", "analogues", activeId];
    $[44] = activeId;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  const t27 = open && activeId != null && (activeRequestedTab === "analogues" || analoguesAreDefaultTab);
  let t28;
  if ($[46] !== activeId) {
    t28 = () => fetchMedicineAnalogues(activeId as string);
    $[46] = activeId;
    $[47] = t28;
  } else {
    t28 = $[47];
  }
  let t29;
  if ($[48] !== t26 || $[49] !== t27 || $[50] !== t28) {
    t29 = {
      queryKey: t26,
      enabled: t27,
      queryFn: t28,
      staleTime: 60000
    };
    $[48] = t26;
    $[49] = t27;
    $[50] = t28;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  const analoguesQuery = useQuery(t29);
  const certificatesQuery = useMedicineCertificates(activeId, open);
  let t30;
  if ($[52] !== detailsQuery.data || $[53] !== t) {
    t30 = (detailsQuery.data ? getMedicineDisplayName(detailsQuery.data) : null) ?? t("medicines.finder.title", {
      defaultValue: "Medicine"
    });
    $[52] = detailsQuery.data;
    $[53] = t;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  const srTitle = t30;
  const [minElapsed, setMinElapsed] = React.useState(false);
  let t31;
  let t32;
  if ($[55] !== activeId || $[56] !== open) {
    t31 = () => {
      if (!open || activeId == null) {
        setMinElapsed(false);
        return;
      }
      setMinElapsed(false);
      const handle = setTimeout(() => setMinElapsed(true), MIN_LOADING_MS);
      return () => clearTimeout(handle);
    };
    t32 = [open, activeId];
    $[55] = activeId;
    $[56] = open;
    $[57] = t31;
    $[58] = t32;
  } else {
    t31 = $[57];
    t32 = $[58];
  }
  React.useEffect(t31, t32);
  const showSkeleton = activeId != null && (detailsQuery.isLoading || !minElapsed);
  let t33;
  if ($[59] !== srTitle) {
    t33 = <DialogTitle className="sr-only">{srTitle}</DialogTitle>;
    $[59] = srTitle;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== t) {
    t34 = t("medicines.details.dialogDescription", {
      defaultValue: "Review medicine characteristics, classifications, variations, analogues, and certificates."
    });
    $[61] = t;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== t34) {
    t35 = <DialogDescription className="sr-only">{t34}</DialogDescription>;
    $[63] = t34;
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  let t36;
  if ($[65] !== activeId || $[66] !== activeRequestedTab || $[67] !== analoguesQuery || $[68] !== certificatesQuery || $[69] !== detailsQuery.data || $[70] !== detailsQuery.error || $[71] !== handleRequestedTabChange || $[72] !== handleSelectAnalogue || $[73] !== handleSelectVariation || $[74] !== icdCodesQuery || $[75] !== showSkeleton || $[76] !== variationsQuery) {
    t36 = activeId == null ? null : showSkeleton ? <MedicineDetailsSkeleton /> : <div className="flex min-h-0 flex-col **:data-[slot=tabs-content]:max-h-[45vh] **:data-[slot=tabs-content]:overflow-y-auto [&>section]:min-h-0 [&>section]:rounded-none [&>section]:shadow-none [&>section]:ring-0"><MedicineDetailsCard details={detailsQuery.data ?? null} loading={false} error={detailsQuery.error instanceof Error ? detailsQuery.error.message : null} icdCodes={icdCodesQuery.data ?? null} variations={variationsQuery.data ?? null} variationsLoading={false} variationsError={variationsQuery.error instanceof Error ? variationsQuery.error.message : null} analogues={analoguesQuery.data ?? null} analoguesLoading={analoguesQuery.isLoading} analoguesError={analoguesQuery.error instanceof Error ? analoguesQuery.error.message : null} certificates={certificatesQuery.data?.data ?? null} certificatesLoading={certificatesQuery.isLoading} certificatesError={certificatesQuery.error instanceof Error ? certificatesQuery.error.message : null} currentMedicineId={activeId} requestedTab={activeRequestedTab} onRequestedTabChange={handleRequestedTabChange} onSelectVariation={handleSelectVariation} onSelectAnalogue={handleSelectAnalogue} /></div>;
    $[65] = activeId;
    $[66] = activeRequestedTab;
    $[67] = analoguesQuery;
    $[68] = certificatesQuery;
    $[69] = detailsQuery.data;
    $[70] = detailsQuery.error;
    $[71] = handleRequestedTabChange;
    $[72] = handleSelectAnalogue;
    $[73] = handleSelectVariation;
    $[74] = icdCodesQuery;
    $[75] = showSkeleton;
    $[76] = variationsQuery;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  let t37;
  if ($[78] !== t33 || $[79] !== t35 || $[80] !== t36) {
    t37 = <DialogContent className="top-25 flex max-h-[calc(100vh-120px)] translate-y-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-[52rem]" overlayClassName="supports-backdrop-filter:backdrop-blur-[2px]">{t33}{t35}{t36}</DialogContent>;
    $[78] = t33;
    $[79] = t35;
    $[80] = t36;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== onOpenChange || $[83] !== open || $[84] !== t37) {
    t38 = <Dialog open={open} onOpenChange={onOpenChange}>{t37}</Dialog>;
    $[82] = onOpenChange;
    $[83] = open;
    $[84] = t37;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  return t38;
}
function MedicineDetailsSkeleton() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="size-28 shrink-0 rounded-lg" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Skeleton className="h-5 w-3/5" />;
    t2 = <Skeleton className="h-4 w-2/5" />;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-col gap-5 p-5"><div className="flex flex-col gap-4 sm:flex-row sm:items-start">{t0}<div className="flex min-w-0 flex-1 flex-col gap-3">{t1}{t2}<div className="flex flex-wrap gap-1.5 pt-1"><Skeleton className="h-6 w-20 rounded-full" /><Skeleton className="h-6 w-16 rounded-full" /><Skeleton className="h-6 w-24 rounded-full" /><Skeleton className="h-6 w-20 rounded-full" /></div></div></div><Skeleton className="h-14 w-full max-w-75 rounded-lg" /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}